import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { LiveVideoService } from '@rsos/live-video';
import { SinatraState } from '@rsos/sinatra';
import normalizeError from '@rsos/sinatra/src/utils/normalizeError';
import { PsapState } from '../capstone/psapInfo/psapInfoSlice';
import { useSelectedCallerID } from '../emergencyCalls';
import { organizationSelector } from '../selectors/organizationSelector';
import { isVT911CoreEnabled } from '../selectors/videoTo911Selectors';

export type VideoSessionState = {
  /**
   * The livekit video room name
   */
  name: string;
  /**
   * The user id of the TC who initiated the session
   * and has control of it
   */
  ownerID: string;
};
export type VideoSessionRole = 'owner' | 'viewer' | null;

export type VideoSessionsMap = {
  [callID: string]: VideoSessionState | undefined;
};
export type VideoSessionFetch = Awaited<
  ReturnType<LiveVideoService['listActiveCalls']>
>;

export const fetchVideoSessions = createAsyncThunk<
  VideoSessionFetch,
  void,
  { state: { sinatra: SinatraState; psaps: PsapState } }
>(
  'videoSessions/fetchVideoSessions',
  async (_, { rejectWithValue, getState }) => {
    const state = getState();
    const orgName = organizationSelector(getState())?.name;
    const hasVT911CoreEnabled = isVT911CoreEnabled(state);

    if (!hasVT911CoreEnabled) {
      return rejectWithValue('Video to 911 Core is not enabled.');
    }

    if (!orgName) return rejectWithValue('No organization to fetch from');

    try {
      const api = new LiveVideoService(orgName);
      return api.listActiveCalls();
    } catch (err) {
      const { message } = normalizeError(err);
      return rejectWithValue(message);
    }
  },
);

const translateAPICalls = (calls: VideoSessionFetch) => {
  const response: VideoSessionsMap = {};
  for (const { call_id, name, owner_id } of calls) {
    response[call_id] = {
      name,
      ownerID: owner_id,
    };
  }
  return response;
};

export type VideoSessionsState = {
  loading: boolean;
  loadingError: null | string;
  videoSessions: VideoSessionsMap;
};

const initialState: VideoSessionsState = {
  loading: false,
  loadingError: null,
  videoSessions: {},
};

const videoSessionsSlice = createSlice({
  name: 'videoSessions',
  initialState,
  reducers: {
    setVideoSession: (
      state,
      action: PayloadAction<{
        callID: string;
        name: string;
        ownerID: string;
      }>,
    ) => {
      state.videoSessions[action.payload.callID] = {
        name: action.payload.name,
        ownerID: action.payload.ownerID,
      };
    },
    removeVideoSession: (state, action: PayloadAction<{ callID: string }>) => {
      delete state.videoSessions[action.payload.callID];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchVideoSessions.fulfilled, (state, action) => {
        state.videoSessions = translateAPICalls(action.payload);
        state.loading = false;
        state.loadingError = null;
      })
      .addCase(fetchVideoSessions.pending, state => {
        state.loading = true;
      })
      .addCase(fetchVideoSessions.rejected, (state, action) => {
        state.loading = false;
        state.loadingError =
          action.error.message || 'Loading video sessions failed.';
      });
  },
});

export const videoSessionsSelector = (state: {
  videoSessions: VideoSessionsState;
}) => state.videoSessions.videoSessions;

export const videoSessionSelector =
  (callID: string) => (state: { videoSessions: VideoSessionsState }) =>
    state.videoSessions.videoSessions[callID];

export const useActiveVideoSession = () => {
  const callID = useSelectedCallerID();
  return useSelector(videoSessionSelector(callID || ''));
};

export const useVideoSessionFor = (callID: string | null) => {
  return useSelector(videoSessionSelector(callID || ''));
};

export const useHasActiveVideoSession = (callID: string | null) => {
  return !!useSelector(videoSessionSelector(callID || ''));
};

export const useMyVideoSessionRole = (
  callID: string | null,
): VideoSessionRole => {
  const myID = useSelector((state: { sinatra: SinatraState }) => {
    return state.sinatra.user.profile?.id;
  });
  const call = useVideoSessionFor(callID);
  if (!call) return null;

  if (call.ownerID === myID) {
    return 'owner';
  } else {
    return 'viewer';
  }
};

export const { setVideoSession, removeVideoSession } =
  videoSessionsSlice.actions;
export const videoSessionsReducer = videoSessionsSlice.reducer;
